import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Avatar,
  Chip,
  Divider,
  InputAdornment,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { authService } from '../services/api';
import { updateUser } from '../store/authSlice';
import { ROLE_LABELS } from './Login';

const Profilo = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [form, setForm] = useState({
    nome: user?.nome || '',
    cognome: user?.cognome || '',
    email: user?.email || '',
    telefono: user?.telefono || '',
  });
  const [pwd, setPwd] = useState({ password_attuale: '', nuova_password: '', conferma: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [msg, setMsg] = useState(null); // { type, text }
  const [loading, setLoading] = useState(false);

  const roles = user?.roles && user.roles.length ? user.roles : [user?.role].filter(Boolean);

  const handleAnagrafica = async () => {
    setMsg(null);
    setLoading(true);
    try {
      const res = await authService.updateProfilo(form);
      dispatch(updateUser(res.data));
      setMsg({ type: 'success', text: 'Profilo aggiornato correttamente.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Errore nel salvataggio.' });
    } finally {
      setLoading(false);
    }
  };

  const handlePassword = async () => {
    setMsg(null);
    if (pwd.nuova_password !== pwd.conferma) {
      setMsg({ type: 'error', text: 'La nuova password e la conferma non coincidono.' });
      return;
    }
    setLoading(true);
    try {
      const res = await authService.updateProfilo({
        password_attuale: pwd.password_attuale,
        nuova_password: pwd.nuova_password,
      });
      dispatch(updateUser(res.data));
      setPwd({ password_attuale: '', nuova_password: '', conferma: '' });
      setMsg({ type: 'success', text: 'Password aggiornata correttamente.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Errore nel cambio password.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ pb: { xs: 10, sm: 4 } }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #3a0000 100%)',
          color: 'white',
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 4 },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: '#fd0000', width: 56, height: 56 }}>
            <PersonIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {user?.nome} {user?.cognome}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {roles.map((r) => (
                <Chip key={r} label={ROLE_LABELS[r] || r} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3, px: { xs: 2, sm: 3 } }}>
        {msg && (
          <Alert severity={msg.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMsg(null)}>
            {msg.text}
          </Alert>
        )}

        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" /> Dati personali
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Username" value={user?.username || ''} disabled helperText="Lo username non è modificabile" />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleAnagrafica} disabled={loading}>
              Salva dati
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon color="primary" /> Cambia password
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password attuale"
                type={showPwd ? 'text' : 'password'}
                value={pwd.password_attuale}
                onChange={(e) => setPwd({ ...pwd, password_attuale: e.target.value })}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPwd(!showPwd)} edge="end" size="small">
                        {showPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nuova password" type={showPwd ? 'text' : 'password'} value={pwd.nuova_password} onChange={(e) => setPwd({ ...pwd, nuova_password: e.target.value })} autoComplete="new-password" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Conferma nuova password" type={showPwd ? 'text' : 'password'} value={pwd.conferma} onChange={(e) => setPwd({ ...pwd, conferma: e.target.value })} autoComplete="new-password" />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ textAlign: 'right' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<LockIcon />}
              onClick={handlePassword}
              disabled={loading || !pwd.password_attuale || !pwd.nuova_password}
            >
              Aggiorna password
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Profilo;
